import { useState, useMemo, useEffect } from 'react';
import { useFormik, FormikProvider } from 'formik';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import ControlPanel from 'components/ControlPanel/ControlPanel';
import { LotDraftFormSchema, LotFormSchema } from '../../../constants';
import LotForm from 'components/LotForm';
import Lot from 'components/Lot';
import { getInitialValues } from 'components/LotForm/utils';
import { prepareRequest } from './utils';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { useStores } from 'stores';
import styles from './styles.module.scss';

const NewLotPage = ({ step }) => {
  const {
    userStore: { userProfile },
    lotStore: { createLotAsync },
    singleLotStore: { setDataForPreview, wipeSingleLot },
  } = useStores();
  const navigate = useNavigate();

  const [editorState, setEditorState] = useState('');
  const [isDraft, setIsDraft] = useState(false);
  const [isPreview, setPreview] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const isFormikReadyToRender = useMemo(() => userProfile.locationId && userProfile.preferredCurrency, [userProfile]);
  let isRunning = false;

  const onSubmit = async values => {
    const formData = prepareRequest(values, isDraft);
    if (isRunning === false) {
      isRunning = true;
      await createLotAsync(formData, navigate);
      setIsDraft(false);
    }
  };

  const validationSchema = useMemo(
  () => (isDraft ? LotDraftFormSchema : LotFormSchema),
  [isDraft]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getInitialValues(step, userProfile.locationId, userProfile.preferredCurrency),
    validationSchema: validationSchema,
    validateOnMount: true,
    onSubmit,
  });

  const { handleSubmit, values } = formik;

  useEffect(() => {
    if (pendingSubmit) {
      handleSubmit();
      setPendingSubmit(false);
    }
  }, [isDraft, pendingSubmit, handleSubmit]);

  const saveHandler = () => {
    setIsDraft(true);
    setPendingSubmit(true);
  };

  const submitHandler = () => {
    setIsDraft(false);
    setPendingSubmit(true);
  };

  const handlePreviewClick = async () => {
    wipeSingleLot();
    setPreview(!isPreview);
    setDataForPreview({ ...values, editorState });
  };

  return (
    <FormikProvider value={formik}>
      <main>
        <section className={styles.bns3_lot_page_control_panel_section}>
          <div className={styles.bns3_mainContainer}>
            <ControlPanel
              isButtonDisabled={isRunning}
              submitHandler={submitHandler}
              saveHandler={saveHandler}
              handlePreviewClick={handlePreviewClick}
              isPreview={isPreview}
            />
          </div>
        </section>

        {isPreview ? (
          <Lot />
        ) : (
          <section className={styles.bns3_form_section}>
            <div className={styles.bns3_mainContainer}>
              {isFormikReadyToRender && (
                <LotForm
                  setEditorState={setEditorState}
                  editorState={editorState}
                />
              )}
            </div>
          </section>
        )}
      </main>
    </FormikProvider>
  );
};

NewLotPage.propTypes = {
  step: PropTypes.number,
  lotStore: PropTypes.shape({
    status: PropTypes.string,
    createLotAsync: PropTypes.func,
  }),
};

export default observer(NewLotPage);
